"use client"
import { useState } from "react"
import { Table, TableBody, TableHeader, TableRow, TableCell } from "@/components/ui/table"
import { getSchemaKeys, getFieldType, getResolvedData } from "../shared/schema-utils"
import { TableCellRenderer } from "./table-cell-renderer"
import { ResizableColumnHeader } from "./resizable-column-header"

export interface TablePreviewProps {
  item: any
  dataSchema: any
  data?: any
  parentData?: any
  parentScope?: string
  draggable?: boolean
  editable?: boolean
  parentPath?: string[]
  onFieldChange?: (path: string[], value: any) => void
  tabIndex?: number
  componentPath?: number[]
  onReorderKeys?: (tabIndex: number, componentPath: number[], fromIndex: number, toIndex: number) => void
}

export function TablePreview({
  item,
  dataSchema,
  data,
  parentData,
  parentScope,
  editable = false,
  parentPath = [],
  onFieldChange,
}: TablePreviewProps) {
  const getCurrentPath = () => {
    const scopeParts = item.scope
      ? item.scope.split("/").filter((p: string) => p && p !== "#" && p !== "properties")
      : []
    return [...parentPath, ...scopeParts]
  }

  const currentPath = getCurrentPath()

  const handleChange = (rowIndex: number, key: string, value: any) => {
    if (onFieldChange) {
      onFieldChange([...currentPath, rowIndex, key], value)
    }
  }

  const handleColumnResize = (key: string, width: number) => {
    setColumnWidths((prev) => ({ ...prev, [key]: width }))
  }

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({})

  const tableData = getResolvedData(item.scope, data, parentData)
  const keys = item.keys && item.keys.length > 0 ? item.keys : getSchemaKeys(item.scope, dataSchema)

  const arrayData = Array.isArray(tableData) ? tableData : []

  return (
    <div className="space-y-2">
      {item.header && <h3 className="ml-2 md:ml-0 text-lg font-semibold">{item.header}</h3>}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {keys.length > 0 ? (
                keys.map((key: string) => (
                  <ResizableColumnHeader
                    key={key}
                    columnKey={key}
                    defaultWidth={columnWidths[key] || 200}
                    onResize={handleColumnResize}
                  >
                    {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                  </ResizableColumnHeader>
                ))
              ) : (
                <ResizableColumnHeader columnKey="empty" defaultWidth={200}>
                  No columns configured
                </ResizableColumnHeader>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {arrayData.length > 0 ? (
              arrayData.map((row: any, rowIndex: number) => (
                <TableRow key={rowIndex}>
                  {keys.map((key: string) => (
                    <TableCellRenderer
                      key={key}
                      row={row}
                      fieldKey={key}
                      rowIndex={rowIndex}
                      editable={editable}
                      fieldType={getFieldType(key, item.scope, dataSchema)}
                      onChange={handleChange}
                      width={columnWidths[key] || 200}
                    />
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={keys.length || 1} className="text-center text-muted-foreground">
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

